import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';

import { Observable, Subject } from 'rxjs';
import { SmartLoaderConfig } from './../config/smart-loader.config';

@Injectable()
export class SmartLoaderService {

    private readonly RequestEndDebounceTime = 150;
    private readonly ExcludedRoutes: Array<string>;
    private readonly ExcludedApis: Array<string>;

    private idFactory = 1;

    private startRequest = new Subject<{ url: string, id: string }>();
    private completeRequest = new Subject<{ url: string, id: string }>();

    private isLoading: Observable<boolean>;

    constructor(private router: Router) {

        this.ExcludedRoutes = SmartLoaderConfig.excludedRoutes.slice();
        this.ExcludedApis = SmartLoaderConfig.excludedApis.slice();

        let routeChanged = this.router.events
            .filter(e => e instanceof NavigationEnd)
            .map((e: NavigationEnd) => e.url)
            .startWith('')
            .shareReplay();

        let requestStarted = this.startRequest
            .withLatestFrom(routeChanged, (req, route) => ({ req: req, route: route }))
            .filter(rs => !this.matchesExclusionPattern(rs.route, rs.req.url))
            .shareReplay();

        let requestCompleted = this.completeRequest
            .withLatestFrom(routeChanged, (req, route) => ({ req: req, route: route }))
            .filter(rc => !this.matchesExclusionPattern(rc.route, rc.req.url))
            .shareReplay();

        let routeActiveRequests = Observable.merge(
                requestStarted.mapTo(1),
                requestStarted.flatMap(rs => Observable.combineLatest(
                        // Wait until request completes
                        requestCompleted.filter(rc => rs.req.id == rc.req.id),
                        // Track # of times the route changes before the request completes
                        routeChanged.scan((changes, _) => changes++, 0).startWith(0),
                        (_, changes) => changes
                    )
                    .take(1)
                    // Reject completed requests outside of current route
                    .filter(routeChanges => routeChanges == 0)
                    .mapTo(-1)
                )
            )
            .scan((count, diff) => count + diff, 0);

        let loading = routeChanged
            .switchMap(e => routeActiveRequests)
            .map(count => count != 0)
            .share();

        let startedLoading = loading.filter(isLoading => isLoading);

        let finishedWithoutRecentStarts = loading
            .filter(isLoading => !isLoading)
            .switchMap(_ => startedLoading
                // Wait to see if a new request won't start in the near future
                .bufferTime(this.RequestEndDebounceTime)
                .take(1)
                .map(starts => starts.length == 0)
            )
            .filter(val => val);

        this.isLoading = Observable.merge(
                startedLoading,
                finishedWithoutRecentStarts.mapTo(false),
                routeChanged.mapTo(false)
            )
            .distinctUntilChanged()
            .shareReplay();

        this.isLoading.subscribe();
    }

    private matchesExclusionPattern(route: string, apiUrl: string): boolean {
        let matches = /^(?:(?:https*:\/\/)*localhost\:\d+|.*?\.(?:com|one)){1}(.+)$/.exec(apiUrl);
        let hasMatch = matches && matches.length == 2;

        return this.ExcludedRoutes.some(r => new RegExp(r).test(route)) ||
            (hasMatch && this.ExcludedApis.some(a => new RegExp(a).test(matches[1])));
    }

    public loading(): Observable<boolean> {
        return this.isLoading;
    }

    public beginRequest(url?: string): string {
        let id = this.generateId().toString();
        this.startRequest.next({ url: url, id: id });
        return id;
    }

    public endRequest(id: string, url?: string) {
        this.completeRequest.next({ url: url, id: id });
    }

    private generateId(): number {
        return this.idFactory++;
    }
}



// WEBPACK FOOTER //
// ./src/app/core/services/smart-loader.service.ts